import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

import { clamp, distance, midpoint, Point2D } from '../pinch-zoom/pinch-zoom.util';

/**
 * Lightweight pinch-zoom + pan + double-tap viewer for a single `<img>`.
 *
 * Uses CSS transforms (no canvas/fabric overhead) so it stays cheap inside
 * lists/swipers with many images. Emits `zoomChange` so parents can disable
 * outer scrollers (e.g. swiper) while the user is zoomed in.
 */
@Component({
  selector: 'itl-zoomable-image',
  imports: [CommonModule],
  templateUrl: './zoomable-image.component.html',
  styleUrls: ['./zoomable-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZoomableImageComponent implements AfterViewInit, OnDestroy {
  @Input() src!: string;
  @Input() alt = '';
  @Input() minZoom = 1;
  @Input() maxZoom = 6;
  /** Zoom factor used when the user double-taps from min zoom. */
  @Input() doubleTapZoom = 2.5;
  /** Enable mouse-wheel / trackpad zoom. Defaults to true. */
  @Input() enableWheelZoom = true;

  /** Emits the new zoom factor whenever it changes. */
  @Output() zoomChange = new EventEmitter<number>();

  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLElement>;

  // Public so the template binding stays simple. Parents must not write these.
  public zoom = 1;
  public translateX = 0;
  public translateY = 0;

  private readonly _activePointers = new Map<number, Point2D>();

  private _pinchStartDistance = 0;
  private _pinchStartZoom = 1;
  private _pinchLastCenter: Point2D | null = null;
  private _isPinching = false;

  private _isPanning = false;
  private _panStartClient: Point2D | null = null;
  private _panStartTranslate: Point2D = { x: 0, y: 0 };

  // Double-tap detection
  private _lastTapAt = 0;
  private _lastTapClient: Point2D | null = null;
  private static readonly DOUBLE_TAP_MS = 300;
  private static readonly DOUBLE_TAP_RADIUS_PX = 30;

  private readonly _onPointerDown = (e: PointerEvent): void => this._handlePointerDown(e);
  private readonly _onPointerMove = (e: PointerEvent): void => this._handlePointerMove(e);
  private readonly _onPointerUp = (e: PointerEvent): void => this._handlePointerUp(e);
  private readonly _onWheel = (e: WheelEvent): void => this._handleWheel(e);

  constructor(private readonly _cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    const el = this.containerRef.nativeElement;
    el.addEventListener('pointerdown', this._onPointerDown);
    el.addEventListener('pointermove', this._onPointerMove);
    el.addEventListener('pointerup', this._onPointerUp);
    el.addEventListener('pointercancel', this._onPointerUp);
    el.addEventListener('pointerleave', this._onPointerUp);
    if (this.enableWheelZoom) {
      el.addEventListener('wheel', this._onWheel, { passive: false });
    }
  }

  ngOnDestroy(): void {
    const el = this.containerRef?.nativeElement;
    if (!el) return;
    el.removeEventListener('pointerdown', this._onPointerDown);
    el.removeEventListener('pointermove', this._onPointerMove);
    el.removeEventListener('pointerup', this._onPointerUp);
    el.removeEventListener('pointercancel', this._onPointerUp);
    el.removeEventListener('pointerleave', this._onPointerUp);
    el.removeEventListener('wheel', this._onWheel);
  }

  /** Reset the view back to its un-zoomed state. */
  public reset(): void {
    this._setTransform(1, 0, 0);
  }

  // ---- Pointer handling -----------------------------------------------------

  private _handlePointerDown(e: PointerEvent): void {
    this._activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this._activePointers.size === 1) {
      this._maybeHandleDoubleTap(e);

      // Allow single-finger pan only when already zoomed in. Otherwise let the
      // parent (e.g. swiper) handle horizontal swipes normally.
      if (this.zoom > this.minZoom + 1e-3) {
        this._isPanning = true;
        this._panStartClient = { x: e.clientX, y: e.clientY };
        this._panStartTranslate = { x: this.translateX, y: this.translateY };
        e.preventDefault();
      }
    }

    if (this._activePointers.size === 2) {
      this._beginPinch();
      this._isPanning = false; // pinch supersedes pan
      e.preventDefault();
    }
  }

  private _handlePointerMove(e: PointerEvent): void {
    if (!this._activePointers.has(e.pointerId)) return;
    this._activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this._isPinching && this._activePointers.size === 2) {
      this._updatePinch();
      e.preventDefault();
      return;
    }

    if (this._isPanning && this._panStartClient && this._activePointers.size === 1) {
      const dx = e.clientX - this._panStartClient.x;
      const dy = e.clientY - this._panStartClient.y;
      this._setTransform(this.zoom, this._panStartTranslate.x + dx, this._panStartTranslate.y + dy);
      e.preventDefault();
    }
  }

  private _handlePointerUp(e: PointerEvent): void {
    this._activePointers.delete(e.pointerId);

    if (this._isPinching && this._activePointers.size < 2) {
      this._endPinch();
    }

    if (this._activePointers.size === 0) {
      this._isPanning = false;
      this._panStartClient = null;
    }
  }

  // ---- Wheel ----------------------------------------------------------------

  private _handleWheel(e: WheelEvent): void {
    e.preventDefault();
    e.stopPropagation();
    const local = this._toLocal({ x: e.clientX, y: e.clientY });
    this._zoomToPoint(this.zoom * 0.999 ** e.deltaY, local);
  }

  // ---- Double-tap -----------------------------------------------------------

  private _maybeHandleDoubleTap(e: PointerEvent): void {
    const now = performance.now();
    const tap: Point2D = { x: e.clientX, y: e.clientY };

    if (
      this._lastTapClient &&
      now - this._lastTapAt < ZoomableImageComponent.DOUBLE_TAP_MS &&
      distance(tap, this._lastTapClient) < ZoomableImageComponent.DOUBLE_TAP_RADIUS_PX
    ) {
      if (this.zoom > this.minZoom + 1e-3) {
        this._setTransform(1, 0, 0);
      } else {
        const local = this._toLocal(tap);
        this._zoomToPoint(this.doubleTapZoom, local);
      }
      this._lastTapAt = 0;
      this._lastTapClient = null;
      return;
    }

    this._lastTapAt = now;
    this._lastTapClient = tap;
  }

  // ---- Pinch ---------------------------------------------------------------

  private _beginPinch(): void {
    const [a, b] = [...this._activePointers.values()];
    this._pinchStartDistance = distance(a, b) || 1;
    this._pinchStartZoom = this.zoom;
    this._pinchLastCenter = this._toLocal(midpoint(a, b));
    this._isPinching = true;
  }

  private _updatePinch(): void {
    const pts = [...this._activePointers.values()];
    if (pts.length !== 2 || !this._pinchLastCenter) return;
    const [a, b] = pts;
    const dist = distance(a, b) || 1;
    const ratio = dist / this._pinchStartDistance;
    const newZoom = clamp(this._pinchStartZoom * ratio, this.minZoom, this.maxZoom);
    const localCenter = this._toLocal(midpoint(a, b));

    // Zoom around the previous midpoint, then translate by midpoint delta.
    this._zoomToPoint(newZoom, this._pinchLastCenter);
    this._setTransform(
      this.zoom,
      this.translateX + (localCenter.x - this._pinchLastCenter.x),
      this.translateY + (localCenter.y - this._pinchLastCenter.y)
    );
    this._pinchLastCenter = localCenter;
  }

  private _endPinch(): void {
    this._isPinching = false;
    this._pinchLastCenter = null;
    if (this.zoom <= this.minZoom + 1e-3) {
      this._setTransform(1, 0, 0);
    }
  }

  // ---- Transform helpers ----------------------------------------------------

  /** Apply a new zoom factor while keeping `local` (container coords) fixed. */
  private _zoomToPoint(newZoom: number, local: Point2D): void {
    const z = clamp(newZoom, this.minZoom, this.maxZoom);
    if (z === this.zoom) return;
    const ratio = z / this.zoom;
    // The image position relative to local point scales by `ratio`.
    const newTx = local.x - (local.x - this.translateX) * ratio;
    const newTy = local.y - (local.y - this.translateY) * ratio;
    this._setTransform(z, newTx, newTy);
  }

  private _toLocal(client: Point2D): Point2D {
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    return {
      x: client.x - (rect.left + rect.width / 2),
      y: client.y - (rect.top + rect.height / 2),
    };
  }

  private _setTransform(zoom: number, tx: number, ty: number): void {
    const z = clamp(zoom, this.minZoom, this.maxZoom);
    const clamped = this._clampTranslate(z, tx, ty);
    const zoomChanged = Math.abs(z - this.zoom) > 1e-4;
    this.zoom = z;
    this.translateX = clamped.tx;
    this.translateY = clamped.ty;
    this._cdr.markForCheck();
    if (zoomChanged) this.zoomChange.emit(this.zoom);
  }

  /**
   * Keep the (scaled) image visually inside its container so the user cannot
   * pan it off-screen entirely. Approximate clamp based on container bounds —
   * adequate UX without measuring rendered image bounds.
   */
  private _clampTranslate(zoom: number, tx: number, ty: number): { tx: number; ty: number } {
    if (zoom <= this.minZoom + 1e-3) return { tx: 0, ty: 0 };
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const maxX = (rect.width * (zoom - 1)) / 2;
    const maxY = (rect.height * (zoom - 1)) / 2;
    return {
      tx: clamp(tx, -maxX, maxX),
      ty: clamp(ty, -maxY, maxY),
    };
  }
}
